/**
 * Which organization an admin is currently administering.
 *
 * `useActiveOrg` is the runtime source of truth for this, and the rule it
 * documents is that the active org is "a persisted selection the user still
 * admins, otherwise the first admin org by created_at — never an arbitrary row".
 *
 * The eligibility, ordering, and selection all live here, as pure functions,
 * because the post-login destination has to reach the same answer *before* any
 * React context exists. Two implementations of "which org" — or two definitions
 * of which membership even counts — would let an admin be assessed against one
 * organization and then dropped into another.
 */

export const ACTIVE_ORG_STORAGE_KEY = "compaire_active_org";

/** The membership roles that make someone an administrator of an organization. */
export const ELIGIBLE_ADMIN_ROLES = ["super_admin", "admin"] as const;

/**
 * The membership roles that give someone an organization *workspace* — i.e. the
 * /admin/* org app, even if only the content/profile corner of it. This is a
 * superset of {@link ELIGIBLE_ADMIN_ROLES}: an editor gets a workspace (so the
 * profile editor and content pages can resolve which org they are editing) but
 * is NOT an administrator. Route-level access to admin-only surfaces is gated
 * separately, on the platform role — a workspace is org context, not authority.
 */
export const ELIGIBLE_WORKSPACE_ROLES = ["super_admin", "admin", "editor"] as const;

/** The minimum an org needs for deterministic ordering and selection. */
export interface SelectableOrg {
  id: string;
  created_at: string;
}

/** The membership fields that decide whether it counts toward active-org selection. */
export interface MembershipEligibility {
  role?: string | null;
  is_current?: boolean | null;
}

/** One membership row: its eligibility fields plus the joined organization. */
export interface AdminMembership<Org extends SelectableOrg> extends MembershipEligibility {
  org: Org | null;
}

/**
 * True when a membership makes the user a *current* administrator.
 *
 * `is_current` must be exactly true: like `.eq("is_current", true)` in SQL, a
 * null is excluded. Production has no null or inactive admin memberships today,
 * so this changes nothing now; it keeps the rule identical across both paths and
 * correct once a backfill or NOT NULL constraint lands (a separate PR).
 */
export const isEligibleAdminMembership = (m: MembershipEligibility): boolean =>
  !!m.role &&
  (ELIGIBLE_ADMIN_ROLES as readonly string[]).includes(m.role) &&
  m.is_current === true;

/**
 * Ascending by created_at, then by id to break ties. Returns a new array; never
 * mutates the input. The id tiebreaker matters because production contains
 * organizations sharing identical creation timestamps, so created_at alone is
 * not a total order.
 */
export const orderAdminOrgs = <T extends SelectableOrg>(orgs: T[]): T[] =>
  [...orgs].sort((a, b) => {
    const byDate = (a.created_at || "").localeCompare(b.created_at || "");
    return byDate !== 0 ? byDate : (a.id || "").localeCompare(b.id || "");
  });

/**
 * The persisted selection when the user still admins it, otherwise the earliest
 * org. Null only when the list is empty.
 */
export const selectActiveOrg = <T extends SelectableOrg>(
  orgs: T[],
  storedId: string | null,
): T | null => {
  if (orgs.length === 0) return null;
  const ordered = orderAdminOrgs(orgs);
  const matched = storedId ? ordered.find((o) => o.id === storedId) : undefined;
  return matched ?? ordered[0];
};

/** Eligible (current admin) orgs from raw membership rows, ordered deterministically. */
export const eligibleAdminOrgs = <Org extends SelectableOrg>(
  memberships: AdminMembership<Org>[],
): Org[] =>
  orderAdminOrgs(
    memberships
      .filter(isEligibleAdminMembership)
      .map((m) => m.org)
      .filter((o): o is Org => !!o),
  );

/**
 * End to end: filter memberships to current admins, order them, and pick the
 * active org. This is the shared definition both the post-login destination and
 * useActiveOrg resolve through, so an inactive or non-admin membership can never
 * become the active organization on either path.
 */
export const selectActiveOrgFromMemberships = <Org extends SelectableOrg>(
  memberships: AdminMembership<Org>[],
  storedId: string | null,
): Org | null => selectActiveOrg(eligibleAdminOrgs(memberships), storedId);

/**
 * True when a membership makes the user a *current* member of an org workspace —
 * super_admin, admin OR editor. Same `is_current === true` rule as
 * {@link isEligibleAdminMembership}; the only difference is the wider role set.
 */
export const isEligibleWorkspaceMembership = (m: MembershipEligibility): boolean =>
  !!m.role &&
  (ELIGIBLE_WORKSPACE_ROLES as readonly string[]).includes(m.role) &&
  m.is_current === true;

/** Eligible (current workspace) orgs from raw membership rows, ordered deterministically. */
export const eligibleWorkspaceOrgs = <Org extends SelectableOrg>(
  memberships: AdminMembership<Org>[],
): Org[] =>
  orderAdminOrgs(
    memberships
      .filter(isEligibleWorkspaceMembership)
      .map((m) => m.org)
      .filter((o): o is Org => !!o),
  );

/**
 * The workspace analogue of {@link selectActiveOrgFromMemberships}: filter to
 * current workspace memberships (including editors), order, and pick the active
 * org. useActiveOrg resolves through this so an editor has an org to edit.
 */
export const selectWorkspaceOrgFromMemberships = <Org extends SelectableOrg>(
  memberships: AdminMembership<Org>[],
  storedId: string | null,
): Org | null => selectActiveOrg(eligibleWorkspaceOrgs(memberships), storedId);

/** Reads the persisted selection, tolerating unavailable storage. */
export const readStoredActiveOrgId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
};
